/**
 * GlobalAuthChip — floating Jubit identity affordance for surfaces with no chrome.
 *
 * jubuddy.com/planet, gghere.com and gghere.com/planet render a full-bleed canvas and
 * mount none of the /jub shells, so they carry no Sidebar, no IconRail and therefore
 * no account UI at all. A visitor on those surfaces had no way to sign in and no way
 * to see who they were signed in as — even though the whole family shares one Jubit
 * account.
 *
 * Deliberately NOT a reuse of `jub/components/AccountChip`: that one is anchored to
 * the Sidebar's `<aside>` positioning context and owns a drawer that opens upward
 * from a bottom-left rail. This is a fixed overlay floating over a 3D scene. Same
 * store, same tier vocabulary (`auth/effective-tier.ts`), different geometry.
 *
 * Signed out it opens the Jubit hand-off — via `userStore.signIn`, which prefers the
 * popup bridge and falls back to the OAuth redirect, so the visitor keeps their place
 * on the planet either way.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useUserStore } from '../../state/userStore.ts';
import { tierPillLabel, tierPillClass, type Role } from '../../auth/effective-tier.ts';
import { SSO_POPUP_NAME } from '../../auth/jubitSsoBridge.ts';
import { SsoConnectingStatus } from '../../auth/SsoConnectingStatus.tsx';
import { useSsoSignIn } from '../../auth/useSsoSignIn.ts';
import { isAuthConfigured } from '../../lib/supabase.ts';

/**
 * Routes that must NOT get the floating chip.
 *
 * Reasons:
 *   - `/jub` already renders its own account UI (Sidebar `AccountChip`, IconRail).
 *   - `/profile` is the ju/credits hub (ADR-0331) and already has `SignInButton`.
 *   - the gameplay canvases are full-bleed and interactive; a fixed overlay in the
 *     top-right can cover HUD or swallow pointer events mid-run.
 */
const CHIP_SUPPRESSED_PREFIXES: readonly string[] = [
  '/jub',
  '/profile',
  '/game',
  '/play',
  '/campaign',
  '/fusion',
  '/heroes',
  '/auth',
  '/embed',
];

/** Pure: whether the floating chip belongs on this path. */
export function shouldShowGlobalAuthChip(pathname: string): boolean {
  return !CHIP_SUPPRESSED_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

/**
 * Route-aware mount. Kept separate from the chip itself so the placement policy is
 * testable without rendering the store-bound component.
 */
export function GlobalAuthChipMount(): JSX.Element | null {
  const { pathname } = useLocation();
  if (!shouldShowGlobalAuthChip(pathname)) return null;
  return <GlobalAuthChip />;
}

export interface AccountMenuLink {
  readonly href: string;
  readonly label: string;
}

/** Where an admin approves or takes down players' planet creations (ADR-0310 / ADR-0318). */
export const ADMIN_QUEUE_HREF = '/admin/planet-creations';

const PROFILE_LINK: AccountMenuLink = { href: '/profile', label: 'Profile' };
const ADMIN_QUEUE_LINK: AccountMenuLink = { href: ADMIN_QUEUE_HREF, label: '🛡 Review planet creations' };

/**
 * Pure: the account menu's links for a role. The review queue is offered to admins only.
 * That is a door, not a lock — the queue's API refuses anyone whose server-side role is not
 * admin — but a link nobody else can use should not be shown to them.
 */
export function accountMenuLinks(role: Role | null | undefined): readonly AccountMenuLink[] {
  return role === 'admin' ? [PROFILE_LINK, ADMIN_QUEUE_LINK] : [PROFILE_LINK];
}

export interface GlobalAuthChipProps {
  /** Extra classes for per-surface placement nudges (e.g. clearing a HUD). */
  className?: string;
}

export function GlobalAuthChip({ className = '' }: GlobalAuthChipProps): JSX.Element | null {
  const status = useUserStore((s) => s.status);
  const email = useUserStore((s) => s.email);
  const profile = useUserStore((s) => s.profile);
  const signOut = useUserStore((s) => s.signOut);
  const effectiveTier = useUserStore((s) => s.effectiveTier);
  const { phase, fallbackHref, start, continueWithOAuth } = useSsoSignIn();
  const ssoBusy = phase === 'opening';

  const [menuOpen, setMenuOpen] = useState(false);
  const [signOutBusy, setSignOutBusy] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Dismiss on outside click / Escape, restoring focus to the trigger so keyboard
  // users are not dropped back at the top of the document.
  useEffect(() => {
    if (!menuOpen) return;
    const onPointer = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setMenuOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMenuOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const handleSignIn = useCallback(async () => {
    // Already waiting on the bridge popup? Re-focus it instead of dead-ending.
    // Closing the popup rejects within 500ms (jubitSsoBridge's closedPoll) and
    // clears `busy`, but a popup left OPEN behind the opener held the pill on
    // "Opening…", disabled, for the full 5-minute TIMEOUT_MS — which reads as
    // "auth is stuck". Shortening that fuse is the wrong cure: the timeout also
    // CLOSES the popup, so a short one would kill a slow-but-real sign-in
    // mid-password. Give the visitor their popup back instead. Re-opening by
    // NAME with an empty URL returns the existing window without navigating it,
    // so an in-progress Google sign-in is not disturbed.
    if (ssoBusy) {
      try {
        window.open('', SSO_POPUP_NAME)?.focus();
      } catch {
        /* popup gone or cross-origin focus refused — the poll will clear busy */
      }
      return;
    }
    await start('google');
  }, [ssoBusy, start]);

  const handleSignOut = useCallback(async () => {
    setMenuOpen(false);
    setSignOutBusy(true);
    try {
      await signOut();
    } finally {
      setSignOutBusy(false);
    }
  }, [signOut]);

  // Nothing to offer when auth is not wired for this environment; rendering a
  // sign-in button that cannot work is worse than rendering nothing.
  if (!isAuthConfigured) return null;

  // 'loading' renders a neutral placeholder rather than flashing "Sign in" at someone
  // who is in fact already signed in.
  if (status === 'loading') {
    return (
      <div
        className={`pointer-events-none fixed right-3 top-3 z-50 h-9 w-24 animate-pulse rounded-full bg-black/30 ${className}`}
        aria-hidden
      />
    );
  }

  if (status !== 'signed-in') {
    return (
      <div ref={rootRef} className={`fixed right-3 top-3 z-50 flex flex-col items-end ${className}`}>
        <button
          type="button"
          onClick={handleSignIn}
          // Deliberately NOT disabled while busy — that was the trap. The click
          // re-focuses the popup we are already waiting on (see handleSignIn).
          title={ssoBusy ? 'Continue in the Jubit sign-in window' : undefined}
          className={`flex items-center gap-2 rounded-full border border-white/15 bg-black/55 px-4 py-2 text-xs font-semibold text-white shadow-lg backdrop-blur transition hover:bg-black/70 ${
            ssoBusy ? 'opacity-70' : ''
          }`}
        >
          <span aria-hidden className="font-bold text-jub-tile">J</span>
          <span>{ssoBusy ? 'Opening… ↗' : 'Sign in with Jubit'}</span>
        </button>
        <SsoConnectingStatus
          phase={phase}
          fallbackHref={fallbackHref}
          onContinueWithOAuth={() => void continueWithOAuth('google')}
        />
      </div>
    );
  }

  const tier = effectiveTier();
  const label = profile?.displayName || email || 'Signed in';
  const initial = (profile?.displayName || email || '?').trim().charAt(0).toUpperCase();

  return (
    // `items-end` matters now that the trigger is a 9x9 circle: the w-56 menu is a
    // block sibling, so without right-alignment it would start at the button's left
    // edge and run off the right of the viewport.
    <div ref={rootRef} className={`fixed right-3 top-3 z-50 flex flex-col items-end ${className}`}>
      {/* AVATAR ONLY. The trigger used to carry the avatar, the full label AND the tier
          pill — and `label` falls back to the EMAIL when a profile has no display name,
          so a signed-in visitor got a ~13rem bar pinned over the planet HUD's top-right
          controls. This component's own header already warns that "a fixed overlay in
          the top-right can cover HUD"; this is that, observed. Identity and tier both
          live in the menu one tap away, and the accessible name still carries them. */}
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setMenuOpen((open) => !open)}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        aria-controls="global-auth-chip-menu"
        title={`${label} · ${tierPillLabel(tier)}`}
        aria-label={`Account: ${label}, ${tierPillLabel(tier)}`}
        className="flex h-9 w-9 items-center justify-center rounded-full border border-white/15 bg-black/55 text-xs text-white shadow-lg backdrop-blur transition hover:bg-black/70"
      >
        {profile?.avatarUrl ? (
          <img
            src={profile.avatarUrl}
            alt=""
            className="h-6 w-6 rounded-full object-cover"
            // A broken avatar URL should degrade to the initial, not a torn image.
            onError={(event) => {
              event.currentTarget.style.display = 'none';
            }}
          />
        ) : (
          <span
            aria-hidden
            className="flex h-6 w-6 items-center justify-center rounded-full bg-jub-tile/25 font-bold"
          >
            {initial}
          </span>
        )}
      </button>

      {menuOpen && (
        <div
          id="global-auth-chip-menu"
          role="menu"
          className="mt-2 w-56 overflow-hidden rounded-xl border border-white/10 bg-neutral-900/95 text-xs text-white shadow-xl backdrop-blur"
        >
          <div className="border-b border-white/10 px-3 py-2">
            <div className="flex items-center gap-2">
              <p className="min-w-0 flex-1 truncate font-semibold">{label}</p>
              {/* The tier moved here when the trigger became avatar-only — it must stay
                  visible SOMEWHERE, and this is where the identity already lives. */}
              <span
                className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-bold ${tierPillClass(tier)}`}
              >
                {tierPillLabel(tier)}
              </span>
            </div>
            {email && label !== email && (
              <p className="truncate text-[11px] text-white/60">{email}</p>
            )}
            <p className="mt-1 text-[11px] text-white/60">
              One Jubit account — works on jubit.ai, dseek.ai and gozayden.com.
            </p>
          </div>
          {accountMenuLinks(profile?.role).map((link) => (
            <a
              key={link.href}
              role="menuitem"
              href={link.href}
              className="block px-3 py-2 transition hover:bg-white/10"
              onClick={() => setMenuOpen(false)}
            >
              {link.label}
            </a>
          ))}
          <button
            role="menuitem"
            type="button"
            onClick={handleSignOut}
            disabled={signOutBusy}
            className="block w-full px-3 py-2 text-left transition hover:bg-white/10 disabled:opacity-60"
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
